// import React, { useEffect, useRef, useState } from "react";
import React, { useEffect, useRef, useState } from "react";
import { FinanceInventoryContext } from "./finance-inventory-context";
import { useDispatch } from "react-redux";
import {
  activityPostFinalEdit,
  attachmentDownloadDelete,
  fetchCommonData,
  getActivityDetail,
  getFilteredInventoryActivity,
  getInventorySupplierFetchAll,
  saveActivityDetail,
  savePermittedInventoryBranches,
  submitActivityDetail,
  uploadActivityAttachment,
  bulkApprovalSubmit,
  rejectActivityDetail
} from "../../../redux/slices";
import {
  ComponentLoaderTypes,
  FinanceInventoryContextType,
  IActivityDetail,
  IActivityItem,
  IAttachment,
  IGetActivityList,
  ILastKey,
  IPageChangeAction,
} from "./finance-inventory-types";
import {
  ALLOWED_MIME_TYPES,
  INVENTORY_ACTIVITY_STATUS,
  INVENTORY_ACTIVITY_TYPES,
  ITEMS_PER_PAGE,
  MAX_FILE_SIZE_KB,
} from "./finance-inventory-constants";
import { ENDPOINT } from "../../../services/apiUrls";
import uuid from "react-native-uuid";
import { ToastNotifySuccess } from "../../../helpers/toast-helper";

const INITIAL_PAGE_NUMBER = 1;

function FinanceInventoryProvider(props: { children: React.ReactElement }) {
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE_NUMBER);
  const [allPagesVisited, setAllPagesVisited] = useState(false);
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [selectedActivityRow, setSelectedActivityRow] = useState<
    IActivityItem | undefined
  >();
  const [activityDetail, setActivityDetail] = useState<
    IActivityDetail | undefined
  >();
  const [activityType, setActivityType] = useState<string>(
    INVENTORY_ACTIVITY_TYPES.goods_received_note
  );
  const [activityListsByPage, setActivityListsByPage] = useState<
    Record<string, IActivityItem[]>
  >({});
  const pagewiseNextKeyRef = useRef<Record<string, ILastKey>>({})
  const [componentLoaders, setComponentLoaders] = useState<ComponentLoaderTypes>({
    GENERAL: false,
    DOWNLOAD: false,
    DELETE: false,
    UPLOAD: false,
    MARK_APPROVED: false,
    EDITING: false,
    REJECTED: false
  })
  const [triggerUseEffect, setTriggerUseEffect] = useState("");
  const currentActivityList = activityListsByPage[pageNumber];
  const midToUseForOperations = activityDetail?.mid || selectedActivityRow?.mid;
  const [activityFilterObj, setActivityFilterObj] = useState<Partial<IGetActivityList>>({});

  useEffect(() => {
    dispatch(
      fetchCommonData({
        url: ENDPOINT.BUSINESS.getInventoryBranches(),
        method: "POST",
        body: { onlyPermittedStores: true },
        action: savePermittedInventoryBranches,
      })
    )
    dispatch(
      getInventorySupplierFetchAll({
        callback: (response: any) => {
          if (response) {
            console.log("Received supplier data:", response);
          } else {
            console.warn("Failed to fetch supplier data");
          }
        }
      })
    );
    
  }, [])

  useEffect(() => {
    if (!selectedActivityRow?.mid) return;
    changeLoaderState({GENERAL: true})
    dispatch(
      getActivityDetail({
        activityId: selectedActivityRow?.mid,
        callback: (response: any) => {
          if (response) {
            setActivityDetail(response);
          } else {
            setActivityDetail(undefined);
          }
          changeLoaderState({GENERAL: false})
        },
      })
    );
  }, [selectedActivityRow]);

  useEffect(() => {
    if (activityListsByPage[pageNumber] || branchIds.length === 0) return;
    getActivityList(activityType, pageNumber, branchIds, true, activityFilterObj)
  }, [activityType, pageNumber, branchIds, allPagesVisited, triggerUseEffect, activityFilterObj]);

  const getActivityList = (activityType: string, pageNumber: number, branchIds: string[], showLoader?: boolean, filters?: {
    fromDate?: string | null;
    toDate?: string | null;
    status?: string;
    activityType?: string;
    postFinalFilter?: string;
    isPostFinalActivityEdited?: boolean;
    orderNumber?: string;
    globalOrderSearch?: string;
    sourceBranchIds?: string[];
    supplierId?: string;
  }
) => {
    const dataToSend: IGetActivityList = {
      activityType: activityType,
      branchIds: branchIds,
      pageSize: ITEMS_PER_PAGE,
      status: INVENTORY_ACTIVITY_STATUS.final,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
      postFinalFilter: filters?.postFinalFilter,
      isPostFinalActivityEdited: filters?.isPostFinalActivityEdited,
      globalOrderSearch: filters?.globalOrderSearch,
      sourceBranchIds: filters?.sourceBranchIds,
      supplierId: filters?.supplierId

    };

    // we take reference of last visited page from 'current page number to visit' - 1
    if (pagewiseNextKeyRef.current[pageNumber-1]) {
      dataToSend.lastKey = pagewiseNextKeyRef.current[pageNumber-1];
    }

    showLoader && changeLoaderState({GENERAL: true})
    dispatch(
      getFilteredInventoryActivity({
        dataToSend,
        callback: (response: any) => {
          if (!response) return;
          pagewiseNextKeyRef.current[pageNumber] = response?.lastKey
          setActivityListsByPage({
            ...activityListsByPage,
            [pageNumber]: response?.results || [],
          });
          if (response?.results?.length < ITEMS_PER_PAGE) {
            setAllPagesVisited(true);
          }
          changeLoaderState({GENERAL: false})
        },
      })
    );
  };

  const refreshCurrentPage = () => {
    getActivityList(activityType, pageNumber, branchIds, false, activityFilterObj)
  }

  const refreshSelectedActivity = () => {
    changeLoaderState({ GENERAL: true });
    dispatch(
      getActivityDetail({
        activityId: selectedActivityRow?.mid,
        callback: (response: any) => {
          if (response) {
            setActivityDetail(response);
          } else {
            setActivityDetail(undefined);
          }
          changeLoaderState({ GENERAL: false });
        },
      })
    );
  };

  function onPageChange(action: IPageChangeAction) {
    if (action === "next" && (!allPagesVisited || pageNumber < Object.keys(activityListsByPage).length)) {
      setPageNumber(pageNumber + 1);
    } else if (action === "prev" && pageNumber > INITIAL_PAGE_NUMBER) {
      setPageNumber(pageNumber - 1);
    }
  }

  const saveInventoryActivity = (activityDetail: IActivityDetail) => {
    if (!activityDetail) return;

    switch(activityDetail.activityType) {
      case INVENTORY_ACTIVITY_TYPES.goods_received_note:
        activityDetail.activityType = INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note;
        break;
      case INVENTORY_ACTIVITY_TYPES.transfer_ins:
        activityDetail.activityType = INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins;
        break;
      default:
        break;
    }

    // because of inconsistancies in backend/frontend structure where itemActivities and items are used interchangeably
    activityDetail.itemActivities = activityDetail.items;

    /**
     * NOTE: we will use the mid of pre approved activity everytime we make an edit.
     * (backend will keep saving it as a draft under the newly created draft activity)
     */
    activityDetail.mid = selectedActivityRow?.mid || "";

    changeLoaderState({EDITING: true})
    dispatch(
      saveActivityDetail({
        activityId: selectedActivityRow?.mid,
        dataToSend: activityDetail,
        callback: (response: IActivityDetail) => {
          if (response) {
            // response.items = response?.itemActivities || [];
            setActivityDetail(response);
            refreshCurrentPage();
          }
          changeLoaderState({EDITING: false})
        },
      })
    );
  };

  const uploadAttachment = (file: File) => {
    const formData = new FormData();
    formData.append("activityId", midToUseForOperations ?? "");
    formData.append("mimeTypes", JSON.stringify(ALLOWED_MIME_TYPES));
    formData.append("maxFileSizeInKB", MAX_FILE_SIZE_KB.toString());
    formData.append("file", file);

    changeLoaderState({UPLOAD: true})
    dispatch(uploadActivityAttachment({
      activityId: midToUseForOperations,
      dataToSend: formData,
      callback: (response: IActivityDetail) => {
        if (response) {
          setActivityDetail({...response, mid: midToUseForOperations as string})
        }
        changeLoaderState({UPLOAD: false})
      }
    }))
  }

  const submitInventoryActivity = (actionCallback: (isSuccess: boolean) => void) => {
    if (!activityDetail) return;

    changeLoaderState({MARK_APPROVED: true})
    dispatch(submitActivityDetail({
      callback: (response: {postFinalActivity: IActivityDetail, activity: IActivityDetail}) => {
        if (response?.postFinalActivity) {
          // if (response?.postFinalActivity?.itemActivities) {
          //   // because of inconsistancies in backend/frontend structure where itemActivities and items are used interchangeably
          //   response.postFinalActivity.items = response?.postFinalActivity?.itemActivities;
          // }
          refreshCurrentPage()
          setActivityDetail(response?.postFinalActivity)
          actionCallback(true)
          ToastNotifySuccess("Activity approved!")
        }
        changeLoaderState({MARK_APPROVED: false})
      },

      // sending the pre approval activity mid will approve the current activity which is in draft
      activityId: selectedActivityRow?.mid,
    }))
  };

  const editPostFinalData = (activityDetail: IActivityDetail) => {
    if (!activityDetail) return;
    activityDetail.mid = midToUseForOperations || ""
    changeLoaderState({EDITING: true})
    dispatch(activityPostFinalEdit({
      callback: (response: IActivityDetail) => {
        if (response) {
          // because of inconsistancies in backend/frontend structure where itemActivities and items are used interchangeably
          // response.items = response?.itemActivities || [];
          setActivityDetail(response)
          refreshCurrentPage()
          ToastNotifySuccess("Activity modified!")
        }
        changeLoaderState({EDITING: false})
      },
      dataToSend: activityDetail,
    }))
  };

  function resetPageData() {
    setPageNumber(INITIAL_PAGE_NUMBER);
    setActivityListsByPage({});
    setAllPagesVisited(false);
    pagewiseNextKeyRef.current = {}
    setTriggerUseEffect(uuid.v4());
    setActivityFilterObj({});
  }

  function changeLoaderState(loaderObject: Partial<ComponentLoaderTypes>) {
    setComponentLoaders((currState) => ({...currState, ...loaderObject}))
  }

  function downloadAttachment(attachment: IAttachment) {
    changeLoaderState({DOWNLOAD: true})
    dispatch(attachmentDownloadDelete({
      activityId: midToUseForOperations,
      method: "GET",
      attachmentId: attachment.id,
      filename: attachment.fileName,
      callback: (response: any) => {
        if (response?.fileURL) {
          window.location.href = `${response?.fileURL}`;
        }
        changeLoaderState({DOWNLOAD: false})
      }
    }))
  }

  function deleteAttachment(attachment: IAttachment) {
    changeLoaderState({DELETE: true})
    dispatch(attachmentDownloadDelete({
      activityId: midToUseForOperations,
      method: "DELETE",
      attachmentId: attachment.id,
      filename: attachment.fileName,
      callback: (response: IActivityDetail) => {
        if (response) {
          setActivityDetail({...response, mid: midToUseForOperations as string})
        }
        changeLoaderState({DELETE: false})
      }
    }))
  }

  const applyFilteredActivity = (filters: Partial<IGetActivityList>) => {
    resetPageData();
    setActivityFilterObj(filters);
  };

  const submitBulkApproval = (ids: string[], onComplete?: () => void) => {
    changeLoaderState({ MARK_APPROVED: true })
    dispatch(bulkApprovalSubmit({
      activityIds: ids,
      callback: (response: { postFinalActivities: IActivityDetail, activity: IActivityDetail, failedIds: IActivityDetail}) => {
        if (response?.postFinalActivities) {
          refreshCurrentPage()
          ToastNotifySuccess("Activity approved!")
          if (selectedActivityRow?.mid) {
            refreshSelectedActivity();
          }
        }
        if (response?.failedIds) {
          ToastNotifySuccess("Activity Approval failed!")
        }
        changeLoaderState({ MARK_APPROVED: false })
        onComplete?.();
      }
    }))

  };

   const rejectInventoryActivity = (actionCallback: (isSuccess: boolean) => void) => {
    if (!activityDetail) return;

    changeLoaderState({REJECTED: true})
    dispatch(rejectActivityDetail({
      callback: (response: IActivityDetail) => {
        if (response) {
          refreshCurrentPage()
          setActivityDetail(response)
          actionCallback(true)
          ToastNotifySuccess("Activity Rejected!")
        }
        changeLoaderState({REJECTED: false})
      },
      activityId: selectedActivityRow?.mid,
    }))
  };

  const featureContextData: FinanceInventoryContextType = {
    activityListsByPage,
    currentActivityList: currentActivityList,
    onPageChange: onPageChange,
    pageNumber: pageNumber,
    activityType,
    setActivityType,
    resetPageData,
    allPagesVisited,
    setBranchIds,
    selectedActivityRow,
    setSelectedActivityRow,
    activityDetail,
    branchIds,
    componentLoaders,
    changeLoaderState,
    uploadAttachment,
    downloadAttachment,
    deleteAttachment,
    saveInventoryActivity,
    submitInventoryActivity,
    editPostFinalData,
    applyFilteredActivity,
    activityFilterObj,
    setActivityFilterObj,
    submitBulkApproval,
    rejectInventoryActivity
  };

  return (
    <FinanceInventoryContext.Provider value={featureContextData}>
      {props.children}
    </FinanceInventoryContext.Provider>
  );
}

export default FinanceInventoryProvider;
